import { convertFileListToFileUIParts, type FileUIPart } from "ai";

export const IMAGE_ATTACHMENT_ACCEPT =
  "image/png,image/jpeg,image/webp" as const;
export const MAX_IMAGES_PER_MESSAGE = 4;
export const MAX_IMAGE_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGE_MESSAGE_BYTES = 12 * 1024 * 1024;
export const MAX_STORED_IMAGE_DATA_BYTES = 20 * 1024 * 1024;
export const MAX_IMAGE_DATA_URL_BYTES =
  Math.ceil((MAX_IMAGE_FILE_BYTES * 4) / 3) + 256;

const SUPPORTED_IMAGE_MEDIA_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
]);

let fallbackAttachmentId = 0;

export type PendingImageAttachment = {
  id: string;
  part: FileUIPart;
  size: number;
};

type MessageLike = {
  role?: unknown;
  parts?: unknown;
};

export function isSupportedImageMediaType(
  mediaType: unknown,
): mediaType is "image/png" | "image/jpeg" | "image/webp" {
  return (
    typeof mediaType === "string" &&
    SUPPORTED_IMAGE_MEDIA_TYPES.has(mediaType.toLowerCase())
  );
}

export function isStoredUserImagePart(value: unknown): value is FileUIPart {
  if (typeof value !== "object" || value === null) return false;
  const part = value as Partial<FileUIPart>;
  if (
    part.type !== "file" ||
    !isSupportedImageMediaType(part.mediaType) ||
    typeof part.url !== "string" ||
    part.url.length > MAX_IMAGE_DATA_URL_BYTES
  ) {
    return false;
  }
  const prefix = `data:${part.mediaType};base64,`;
  if (!part.url.startsWith(prefix)) return false;
  const payload = part.url.slice(prefix.length);
  if (
    !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(
      payload,
    )
  ) {
    return false;
  }
  return (
    imagePayloadDecodedBytes(payload) <= MAX_IMAGE_FILE_BYTES &&
    hasExpectedImageSignature(payload, part.mediaType)
  );
}

export function imagePartDataBytes(part: FileUIPart): number {
  return part.url.length;
}

export function measureUserImageDataBytes(
  messages: readonly MessageLike[],
): number {
  let bytes = 0;
  for (const message of messages) {
    if (message.role !== "user" || !Array.isArray(message.parts)) continue;
    for (const part of message.parts) {
      if (isStoredUserImagePart(part)) bytes += imagePartDataBytes(part);
    }
  }
  return bytes;
}

export function validateStoredUserImageParts(parts: readonly unknown[]): void {
  const images: FileUIPart[] = [];
  for (const part of parts) {
    if (
      typeof part !== "object" ||
      part === null ||
      (part as { type?: unknown }).type !== "file"
    ) {
      continue;
    }
    if (!isStoredUserImagePart(part)) {
      throw new Error("对话包含无效或不受支持的图片附件。");
    }
    images.push(part);
  }

  if (images.length > MAX_IMAGES_PER_MESSAGE) {
    throw new Error(`每轮最多附带 ${MAX_IMAGES_PER_MESSAGE} 张图片。`);
  }
  const decodedBytes = images.reduce(
    (total, image) => total + imagePartDecodedBytes(image),
    0,
  );
  if (decodedBytes > MAX_IMAGE_MESSAGE_BYTES) {
    throw new Error("单轮图片合计不能超过 12MB。");
  }
}

export function validateImageFiles(
  files: readonly File[],
  pending: readonly PendingImageAttachment[],
): void {
  if (pending.length + files.length > MAX_IMAGES_PER_MESSAGE) {
    throw new Error(`每轮最多附带 ${MAX_IMAGES_PER_MESSAGE} 张图片。`);
  }

  let selectedBytes = 0;
  for (const file of files) {
    if (!resolveImageMediaType(file)) {
      throw new Error("仅支持 PNG、JPEG 和 WebP 图片。");
    }
    if (file.size <= 0) {
      throw new Error(`图片“${file.name || "未命名图片"}”为空。`);
    }
    if (file.size > MAX_IMAGE_FILE_BYTES) {
      throw new Error(`图片“${file.name || "未命名图片"}”超过单张 5MB 上限。`);
    }
    selectedBytes += file.size;
  }

  const pendingBytes = pending.reduce(
    (total, attachment) => total + attachment.size,
    0,
  );
  if (pendingBytes + selectedBytes > MAX_IMAGE_MESSAGE_BYTES) {
    throw new Error("单轮图片合计不能超过 12MB。");
  }
}

export async function prepareImageAttachments(
  files: FileList,
  pending: readonly PendingImageAttachment[],
  storedImageDataBytes: number,
): Promise<PendingImageAttachment[]> {
  const selected = Array.from(files);
  validateImageFiles(selected, pending);
  const parts = await convertFileListToFileUIParts(files);
  const attachments = parts.map((part, index) => {
    const file = selected[index]!;
    const mediaType = resolveImageMediaType(file)!;
    const payloadStart = part.url.indexOf("base64,");
    if (payloadStart < 0) {
      throw new Error(`无法读取图片“${file.name || "未命名图片"}”。`);
    }
    const attachment = {
      id: createAttachmentId(),
      part: {
        ...part,
        filename: file.name || `image-${index + 1}`,
        mediaType,
        url: `data:${mediaType};base64,${part.url.slice(payloadStart + 7)}`,
      },
      size: file.size,
    } satisfies PendingImageAttachment;
    if (!isStoredUserImagePart(attachment.part)) {
      throw new Error(
        `图片“${file.name || "未命名图片"}”内容无效或与格式不符。`,
      );
    }
    return attachment;
  });
  validateStoredUserImageParts([
    ...pending.map((attachment) => attachment.part),
    ...attachments.map((attachment) => attachment.part),
  ]);

  const pendingDataBytes = pending.reduce(
    (total, attachment) => total + imagePartDataBytes(attachment.part),
    0,
  );
  const selectedDataBytes = attachments.reduce(
    (total, attachment) => total + imagePartDataBytes(attachment.part),
    0,
  );
  if (
    storedImageDataBytes + pendingDataBytes + selectedDataBytes >
    MAX_STORED_IMAGE_DATA_BYTES
  ) {
    throw new Error(
      "当前项目的对话图片已接近 20MB 上限，请删除旧会话或改用更小的图片。",
    );
  }

  return attachments;
}

export function imageAttachmentLabel(part: unknown): string | null {
  if (!isStoredUserImagePart(part)) return null;
  const filename =
    typeof part.filename === "string" ? part.filename.trim() : "";
  return filename || "图片参考";
}

function resolveImageMediaType(file: File): string | null {
  const declared = file.type.toLowerCase();
  if (isSupportedImageMediaType(declared)) return declared;
  if (declared === "image/jpg") return "image/jpeg";
  if (declared && declared !== "application/octet-stream") return null;

  const filename = file.name.toLowerCase();
  if (filename.endsWith(".png")) return "image/png";
  if (filename.endsWith(".jpg") || filename.endsWith(".jpeg"))
    return "image/jpeg";
  if (filename.endsWith(".webp")) return "image/webp";
  return null;
}

function imagePartDecodedBytes(part: FileUIPart): number {
  return imagePayloadDecodedBytes(part.url.slice(part.url.indexOf(",") + 1));
}

function imagePayloadDecodedBytes(payload: string): number {
  if (!payload) return 0;
  const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
  return (payload.length / 4) * 3 - padding;
}

function hasExpectedImageSignature(
  payload: string,
  mediaType: string,
): boolean {
  let bytes: Uint8Array;
  try {
    const binary = globalThis.atob(
      payload.slice(0, Math.min(48, payload.length)),
    );
    bytes = Uint8Array.from(binary, (value) => value.charCodeAt(0));
  } catch {
    return false;
  }

  switch (mediaType.toLowerCase()) {
    case "image/png":
      return (
        bytes.length >= 24 &&
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47 &&
        bytes[4] === 0x0d &&
        bytes[5] === 0x0a &&
        bytes[6] === 0x1a &&
        bytes[7] === 0x0a &&
        bytes[12] === 0x49 &&
        bytes[13] === 0x48 &&
        bytes[14] === 0x44 &&
        bytes[15] === 0x52 &&
        bytes.slice(16, 20).some((value) => value !== 0) &&
        bytes.slice(20, 24).some((value) => value !== 0)
      );
    case "image/jpeg":
      return (
        bytes.length >= 4 &&
        bytes[0] === 0xff &&
        bytes[1] === 0xd8 &&
        bytes[2] === 0xff
      );
    case "image/webp":
      return (
        bytes.length >= 16 &&
        bytes[0] === 0x52 &&
        bytes[1] === 0x49 &&
        bytes[2] === 0x46 &&
        bytes[3] === 0x46 &&
        bytes[8] === 0x57 &&
        bytes[9] === 0x45 &&
        bytes[10] === 0x42 &&
        bytes[11] === 0x50
      );
    default:
      return false;
  }
}

function createAttachmentId(): string {
  if (
    typeof globalThis.crypto !== "undefined" &&
    "randomUUID" in globalThis.crypto
  ) {
    return globalThis.crypto.randomUUID();
  }
  fallbackAttachmentId += 1;
  return `attachment-${Date.now().toString(36)}-${fallbackAttachmentId.toString(36)}`;
}
